import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import {
  LocationsStockModal,
  LocationsStockModalRef,
} from '@modules/Packaging/materialAlmacen/components/modals/LocationsStockModal';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';
import { DynamicCombobox } from '@shared/components/ui/DynamicCombobox';
import { Alert, AlertDescription } from '@shared/components/ui/shadcn/alert';
import { useCreatePackaging } from '../../hooks/useCreatePackaging';
import { useUpdatePackaging } from '../../hooks/useUpdatePackaging';
import { PackagingCategory } from '../../models/Packaging';
import { PackagingItem } from '../../models/Packaging';
import { usePackagingBoxGroups } from '../../hooks/usePackagingBoxGroups';
import { useAuthStore } from '@shared/stores/authStore';
import { useTenantChildDynamicCombobox } from '@shared/hooks/useTenantChildDynamicCombobox';
import { useCreatePackagingBoxGroup } from '../../hooks/useCreatePackagingBoxGroup';
import { PackagingDimensionsFields } from '@modules/Packaging/shared/components/PackagingDimensionsFields';
import { EMPTY_DIMENSION_FORM } from '@modules/Packaging/shared/types/dimensions';
import type { PackagingDimensionFormState } from '@modules/Packaging/shared/types/dimensions';
import {
  buildPackagingDimensionsPayload,
  dimensionFormFromItem,
} from '@modules/Packaging/shared/utils/packagingDimensions';

type MaterialType = 'customer_box' | 'marketing';
type ModalMode = 'create' | 'edit';

export interface PackagingModalsRef {
  openCreateModal: () => void;
  openEditModal: (item: PackagingItem) => void;
  openAdjustStockModal: (item: PackagingItem) => void;
}

interface PackagingModalsProps {
  onRefetch?: () => void;
}

const PackagingModals = forwardRef<PackagingModalsRef, PackagingModalsProps>(({ onRefetch }, ref) => {
  const createModalRef = useRef<ModalCenterRef>(null);
  const locationsStockModalRef = useRef<LocationsStockModalRef>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation('packagingModule');
  const team = useAuthStore((state) => state.team);
  const isTenant3pl = team?.type === 'tenant_3pl';

  // Estado del formulario de creación
  const [mode, setMode] = useState<ModalMode>('create');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [selectedTenantChildId, setSelectedTenantChildId] = useState<number | null>(null);
  const [materialType, setMaterialType] = useState<MaterialType>('customer_box');
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [pvp, setPvp] = useState('');
  const [selectedBoxGroupId, setSelectedBoxGroupId] = useState<number | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [existingImageName, setExistingImageName] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<PackagingDimensionFormState>(EMPTY_DIMENSION_FORM);
  const [initialDimensions, setInitialDimensions] = useState<PackagingDimensionFormState>(EMPTY_DIMENSION_FORM);
  const { createBoxGroupAsync, isCreatingBoxGroup } = useCreatePackagingBoxGroup();

  const { createPackaging, isCreating } = useCreatePackaging({
    onSuccess: () => {
      createModalRef.current?.closeModal();
      resetForm();
      onRefetch?.();
    },
  });

  const { updatePackaging, isUpdating } = useUpdatePackaging({
    onSuccess: () => {
      createModalRef.current?.closeModal();
      resetForm();
      onRefetch?.();
    },
  });

  const resetForm = () => {
    setMode('create');
    setEditingId(null);
    setSelectedTenantChildId(null);
    setMaterialType('customer_box');
    setName('');
    setSku('');
    setPvp('');
    setSelectedBoxGroupId(null);
    setImageFile(null);
    setExistingImageName(null);
    setExistingImageUrl(null);
    setPreviewImageUrl(null);
    setDimensions(EMPTY_DIMENSION_FORM);
    setInitialDimensions(EMPTY_DIMENSION_FORM);
  };

  const getImageNameFromUrl = (imageUrl: string | null): string | null => {
    if (!imageUrl) return null;
    const rawName = imageUrl.split('/').pop();
    if (!rawName) return null;
    return decodeURIComponent(rawName);
  };

  const mapMaterialTypeToCategory = (type: MaterialType): PackagingCategory => {
    if (type === 'marketing') return 'marketing_item';
    return type; // 'customer_box'
  };

  const isCreateFor3pl = mode === 'create' && isTenant3pl;

  const {
    searchFn,
    onLoadMore,
    hasMore,
    isLoadingBrowse,
    isFetchedBrowse,
    browseTotal,
    resolveSelectedOption,
    fetchError: tenantChildrenError,
  } = useTenantChildDynamicCombobox({
    parentTeamId: team?.id,
    enabled: isCreateFor3pl,
  });

  const hasClientOptions = (browseTotal ?? 1) > 0;
  const listEmptyAfterFetch = Boolean(
    isCreateFor3pl && isFetchedBrowse && browseTotal !== undefined && browseTotal === 0,
  );

  // En tenant_3pl, tanto en creación como en edición debemos filtrar box-groups
  // por el team seleccionado/asociado al material.
  const boxGroupTeamId = isTenant3pl ? selectedTenantChildId : (team?.id ?? null);
  const shouldLoadBoxGroups = !isTenant3pl || !!selectedTenantChildId;
  const { boxGroups, isLoading: isBoxGroupsLoading } = usePackagingBoxGroups({
    teamId: boxGroupTeamId,
    enabled: shouldLoadBoxGroups,
  });

  const showForm = !isCreateFor3pl || selectedTenantChildId !== null;
  const isBoxMaterial = materialType === 'customer_box';
  const isBoxGroupRequiredAndMissing = isBoxMaterial && !selectedBoxGroupId;
  const isCreatingBoxGroupLoading = isCreatingBoxGroup;

  const handleSubmit = () => {
    if (isCreateFor3pl && !selectedTenantChildId) {
      return;
    }

    const category = mapMaterialTypeToCategory(materialType);
    const dimensionsPayload = buildPackagingDimensionsPayload(dimensions, category, mode, initialDimensions);

    const payload = {
      name,
      sku,
      category,
      ...(selectedTenantChildId !== null && selectedTenantChildId !== undefined
        ? { team_id: selectedTenantChildId }
        : {}),
      ...(pvp ? { pvp: parseFloat(pvp) } : {}),
      ...(selectedBoxGroupId ? { packaging_box_group_id: selectedBoxGroupId } : {}),
      ...(imageFile ? { image_hash: imageFile } : {}),
      ...dimensionsPayload,
    };

    if (mode === 'edit' && editingId) {
      updatePackaging(editingId, payload);
      return;
    }

    createPackaging(payload);
  };

  const handleCreateBoxGroup = async (inputName: string) => {
    if (!boxGroupTeamId) {
      return;
    }

    const createdGroup = await createBoxGroupAsync({
      name: inputName,
      teamId: boxGroupTeamId,
    });

    setSelectedBoxGroupId(createdGroup.id);
  };

  useImperativeHandle(ref, () => ({
    openCreateModal: () => {
      resetForm();
      createModalRef.current?.openModal();
    },
    openAdjustStockModal: (item: PackagingItem) => {
      locationsStockModalRef.current?.open(item);
    },
    openEditModal: (item: PackagingItem) => {
      resetForm();
      setMode('edit');
      setEditingId(item.id);
      // En algunos listados llega team.id pero no team_id directo.
      setSelectedTenantChildId(item.team_id ?? item.team?.id ?? null);
      setName(item.name);
      setSku(item.code);
      setPvp(item.pvp !== null && item.pvp !== undefined ? String(item.pvp) : '');
      setMaterialType(item.category === 'marketing_item' ? 'marketing' : 'customer_box');
      setSelectedBoxGroupId(item.packaging_box_group_id ?? null);
      setExistingImageName(getImageNameFromUrl(item.image_url));
      setExistingImageUrl(item.image_url);
      const loadedDimensions = dimensionFormFromItem(
        item.folded_dimensions,
        item.assembled_dimensions,
        item.dimension_unit,
      );
      setDimensions(loadedDimensions);
      setInitialDimensions(loadedDimensions);
      createModalRef.current?.openModal();
    },
  }));

  useEffect(() => {
    if (!imageFile) {
      setPreviewImageUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(imageFile);
    setPreviewImageUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [imageFile]);

  const currentImageSrc = previewImageUrl || existingImageUrl;

  return (
    <>
      {/* Modal de stock */}
      <LocationsStockModal ref={locationsStockModalRef} onSuccess={onRefetch} />

      {/* Modal de creación de material */}
      <ModalCenter
        ref={createModalRef}
        title={mode === 'edit' ? t('modal.edit.title') : t('modal.create.title')}
        width="xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => createModalRef.current?.closeModal()}
              data-testid="packaging-cancel-button"
            >
              {t('button.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={
                isCreating ||
                isUpdating ||
                !name ||
                !sku ||
                (isCreateFor3pl && !selectedTenantChildId) ||
                isBoxGroupRequiredAndMissing
              }
              data-testid="packaging-save-button"
            >
              {isCreating || isUpdating
                ? mode === 'edit'
                  ? t('button.updating')
                  : t('button.creating')
                : mode === 'edit'
                  ? t('button.update')
                  : t('button.create')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {isCreateFor3pl && (
            <div className="border border-card-border rounded-lg p-4 shadow-sm">
              <div>
                <h2 className="text-sm font-semibold text-title">{t('clientSelector.title')}</h2>
                <p className="text-sm text-complementary-text mt-1">{t('clientSelector.description')}</p>
              </div>
              <div className="mt-4">
                <DynamicCombobox
                  value={selectedTenantChildId ? String(selectedTenantChildId) : ''}
                  setValue={(value) => setSelectedTenantChildId(value ? parseInt(value, 10) : null)}
                  placeholder={
                    isCreateFor3pl && hasClientOptions ? t('clientSelector.placeholder') : t('clientSelector.empty')
                  }
                  searchFn={searchFn}
                  resolveSelectedOption={resolveSelectedOption}
                  onLoadMore={onLoadMore}
                  hasMore={hasMore}
                  className="w-full"
                  disabled={
                    isCreatingBoxGroupLoading || listEmptyAfterFetch || (isLoadingBrowse && !selectedTenantChildId)
                  }
                  dataTestId="packaging-modal-client-select"
                />
              </div>
              <div className="mt-4 space-y-2">
                {tenantChildrenError && (
                  <Alert className="border-error text-error">
                    <AlertDescription>{tenantChildrenError}</AlertDescription>
                  </Alert>
                )}
                {listEmptyAfterFetch && !tenantChildrenError && (
                  <Alert className="border-warning text-warning">
                    <AlertDescription>{t('clientSelector.emptyDescription')}</AlertDescription>
                  </Alert>
                )}
              </div>
            </div>
          )}

          {showForm ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Selector de tipo de material */}
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-text mb-1 block">
                  {t('modal.create.material_type_label')}
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    className={`w-full border rounded-lg p-3 text-left transition-colors ${
                      materialType === 'customer_box' ? 'border-lines bg-card-bg' : 'border-card-border bg-body-bg'
                    }`}
                    onClick={() => setMaterialType('customer_box')}
                    disabled={isCreatingBoxGroupLoading}
                    data-testid="packaging-type-customer-box"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          materialType === 'customer_box' ? 'border-text' : 'border-lines'
                        }`}
                      >
                        {materialType === 'customer_box' && <span className="w-2 h-2 rounded-full bg-text" />}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-text">
                          {t('modal.create.material_type_customer_box_title')}
                        </p>
                        <p className="text-xs text-subtext">
                          {t('modal.create.material_type_customer_box_description')}
                        </p>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    className={`w-full border rounded-lg p-3 text-left transition-colors ${
                      materialType === 'marketing' ? 'border-lines bg-card-bg' : 'border-card-border bg-body-bg'
                    }`}
                    onClick={() => {
                      setMaterialType('marketing');
                      setSelectedBoxGroupId(null);
                      setDimensions(EMPTY_DIMENSION_FORM);
                    }}
                    disabled={isCreatingBoxGroupLoading}
                    data-testid="packaging-type-marketing"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                          materialType === 'marketing' ? 'border-text' : 'border-lines'
                        }`}
                      >
                        {materialType === 'marketing' && <span className="w-2 h-2 rounded-full bg-text" />}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-text">
                          {t('modal.create.material_type_marketing_title')}
                        </p>
                        <p className="text-xs text-subtext">{t('modal.create.material_type_marketing_description')}</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {isBoxMaterial && (
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium text-text mb-1 block">
                    {t('modal.create.box_group_label')}
                  </label>
                  <Combobox
                    options={boxGroups.map((group) => ({
                      label: group.name,
                      value: String(group.id),
                    }))}
                    value={selectedBoxGroupId ? String(selectedBoxGroupId) : ''}
                    setValue={(value) => setSelectedBoxGroupId(value ? parseInt(value, 10) : null)}
                    placeholder={t('modal.create.box_group_placeholder')}
                    disabled={isBoxGroupsLoading || isCreatingBoxGroupLoading}
                    className="w-full"
                    dataTestId="packaging-box-group-select"
                    onCreateOption={handleCreateBoxGroup}
                    createOptionLabel={(input) =>
                      input
                        ? t('modal.create.box_group_create_option', { value: input })
                        : t('modal.create.box_group_create_option_idle')
                    }
                    isCreatingOption={isCreatingBoxGroup}
                  />
                  {/* Ayuda creación/edición grupo — Box group helper */}
                  <p className="text-xs text-subtext leading-snug">{t('modal.create.box_group_helper')}</p>
                </div>
              )}

              {/* Campo nombre */}
              <div>
                <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.name_label')}</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('modal.create.name_placeholder')}
                  disabled={isCreatingBoxGroupLoading}
                  data-testid="modal-crear-pack-title"
                />
              </div>

              {/* Campo SKU */}
              <div>
                <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.sku_label')}</label>
                <Input
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  placeholder={t('modal.create.sku_placeholder')}
                  disabled={isCreatingBoxGroupLoading}
                  data-testid="packaging-sku-input"
                />
              </div>

              {/* Campo PVP */}
              <div>
                <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.pvp_label')}</label>
                <Input
                  type="number"
                  value={pvp}
                  onChange={(e) => setPvp(e.target.value)}
                  placeholder={t('modal.create.pvp_placeholder')}
                  min="0"
                  step="0.01"
                  disabled={isCreatingBoxGroupLoading}
                  data-testid="packaging-pvp-input"
                />
              </div>

              {isBoxMaterial && (
                <PackagingDimensionsFields
                  state={dimensions}
                  onChange={setDimensions}
                  disabled={isCreatingBoxGroupLoading}
                  labels={{
                    sectionTitle: t('modal.create.dimensions_section_title'),
                    foldedTitle: t('modal.create.dimensions_folded_title'),
                    assembledTitle: t('modal.create.dimensions_assembled_title'),
                    length: t('modal.create.dimensions_length'),
                    width: t('modal.create.dimensions_width'),
                    height: t('modal.create.dimensions_height'),
                    unitCm: t('modal.create.dimensions_unit_cm'),
                    unitMt: t('modal.create.dimensions_unit_mt'),
                  }}
                />
              )}

              {/* Campo imagen */}
              <div>
                <label className="text-sm font-medium text-text mb-1 block">{t('modal.create.image_label')}</label>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                  className="hidden"
                  disabled={isCreatingBoxGroupLoading}
                  onChange={(e) => {
                    const selectedFile = e.target.files?.[0] ?? null;
                    setImageFile(selectedFile);
                    if (selectedFile) {
                      setExistingImageName(null);
                    }
                  }}
                  data-testid="packaging-image-input"
                />
                <div className={`border border-card-border rounded-md bg-body-bg ${mode === 'edit' ? 'p-3' : ''}`}>
                  {mode === 'edit' ? (
                    <div className="flex items-center justify-between gap-3">
                      <div
                        className="w-10 h-10 rounded-md border border-card-border overflow-hidden bg-body-bg flex items-center justify-center"
                        data-testid="packaging-image-preview"
                      >
                        {currentImageSrc ? (
                          <img
                            src={currentImageSrc}
                            alt={imageFile?.name || existingImageName || t('modal.create.image_label')}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-[10px] text-subtext">{t('modal.create.image_placeholder')}</span>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={isCreatingBoxGroupLoading}
                        data-testid="packaging-image-select-button"
                      >
                        Cambiar imagen
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2 h-10">
                      <span className="text-sm text-subtext truncate pl-3 pr-3">
                        {imageFile?.name || t('modal.create.image_placeholder')}
                      </span>
                      <div className="flex items-center h-full shrink-0">
                        {imageFile && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setImageFile(null);
                              if (imageInputRef.current) {
                                imageInputRef.current.value = '';
                              }
                            }}
                            disabled={isCreatingBoxGroupLoading}
                            data-testid="packaging-image-clear-button"
                            className="h-full rounded-none border-0 border-l border-card-border px-3"
                          >
                            {t('modal.create.image_remove')}
                          </Button>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={isCreatingBoxGroupLoading}
                          data-testid="packaging-image-select-button"
                          className="h-full rounded-none border-0 border-l border-card-border px-3"
                        >
                          {t('modal.create.image_select')}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </ModalCenter>
    </>
  );
});

PackagingModals.displayName = 'PackagingModals';

export default PackagingModals;
